import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/components/nutrimart/types";
import type { BankAccount, PaymentSettings } from "@/lib/payments/types";
import { DEFAULT_PAYMENT_SETTINGS } from "@/lib/payments/types";

/**
 * Admin editor for the singleton `payment_settings` row.
 * Drives what shows up in checkout (bank accounts list, COD eligibility).
 */
export function PaymentSettingsEditor({ lang }: { lang: Lang }) {
  const [s, setS] = useState<PaymentSettings>(DEFAULT_PAYMENT_SETTINGS);
  const [adminEmail, setAdminEmail] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [citiesText, setCitiesText] = useState("");

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("payment_settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (error) console.error("settings read", error);
      if (data) {
        const next: PaymentSettings = {
          bank_accounts: (data.bank_accounts as unknown as BankAccount[]) ?? [],
          cod_enabled: data.cod_enabled,
          cod_surcharge: Number(data.cod_surcharge ?? 0),
          cod_allowed_cities: data.cod_allowed_cities ?? [],
          azul_enabled: data.azul_enabled,
          manual_transfer_enabled: data.manual_transfer_enabled,
          express_free_threshold_usd: Number(
            (data as any).express_free_threshold_usd ??
              DEFAULT_PAYMENT_SETTINGS.express_free_threshold_usd,
          ),
          express_free_note_es:
            (data as any).express_free_note_es ??
            DEFAULT_PAYMENT_SETTINGS.express_free_note_es,
          express_free_note_en:
            (data as any).express_free_note_en ??
            DEFAULT_PAYMENT_SETTINGS.express_free_note_en,
        };
        setS(next);
        setCitiesText(next.cod_allowed_cities.join(", "));
      }
      // Admin email lives in the admin-only table now.
      const { data: priv } = await supabase
        .from("payment_admin_settings")
        .select("admin_notification_email")
        .eq("id", 1)
        .maybeSingle();
      setAdminEmail((priv as any)?.admin_notification_email ?? "");
      setLoading(false);
    })();
  }, []);

  function updateAcct(idx: number, patch: Partial<BankAccount>) {
    setS((p) => ({
      ...p,
      bank_accounts: p.bank_accounts.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  }
  function addAcct() {
    setS((p) => ({
      ...p,
      bank_accounts: [
        ...p.bank_accounts,
        { bank: "", account_type: "Corriente", account_number: "", holder: "", rnc: "" },
      ],
    }));
  }
  function removeAcct(idx: number) {
    setS((p) => ({ ...p, bank_accounts: p.bank_accounts.filter((_, i) => i !== idx) }));
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    const cities = citiesText
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    const payload = {
      bank_accounts: s.bank_accounts as unknown as never,
      cod_enabled: s.cod_enabled,
      cod_surcharge: s.cod_surcharge,
      cod_allowed_cities: cities,
      azul_enabled: s.azul_enabled,
      manual_transfer_enabled: s.manual_transfer_enabled,
      express_free_threshold_usd: Number(s.express_free_threshold_usd) || 0,
      express_free_note_es: s.express_free_note_es?.trim() || null,
      express_free_note_en: s.express_free_note_en?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from("payment_settings").update(payload).eq("id", 1);
    const { error: e2 } = await supabase
      .from("payment_admin_settings")
      .upsert({ id: 1, admin_notification_email: adminEmail.trim() || null }, { onConflict: "id" });
    setSaving(false);
    if (error || e2) {
      console.error("settings save", error);
      setMsg(lang === "es" ? "Error al guardar" : "Save failed");
    } else {
      setS((p) => ({ ...p, cod_allowed_cities: cities }));
      setMsg(lang === "es" ? "✓ Guardado" : "✓ Saved");
      setTimeout(() => setMsg(null), 2500);
    }
  }

  if (loading) return <p style={{ color: "var(--tx3)" }}>{lang === "es" ? "Cargando..." : "Loading..."}</p>;

  const inp: React.CSSProperties = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid var(--bd2)",
    borderRadius: 6,
    background: "var(--bg2)",
    color: "var(--tx)",
    fontSize: 12,
    fontFamily: "var(--f)",
  };
  const lbl: React.CSSProperties = { fontSize: 10, color: "var(--tx3)", fontWeight: 600, display: "block", marginBottom: 3 };

  return (
    <div
      data-testid="payment-settings-editor"
      style={{
        border: "1px solid var(--bd2)",
        borderRadius: "var(--r2)",
        padding: 14,
        marginBottom: 18,
        background: "var(--bg2)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{lang === "es" ? "Configuración de pagos" : "Payment settings"}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {msg && <span style={{ fontSize: 11, color: "var(--tx2)" }}>{msg}</span>}
          <button className="btn bp bs" type="button" disabled={saving} onClick={save}>
            {saving ? (lang === "es" ? "Guardando..." : "Saving...") : lang === "es" ? "Guardar" : "Save"}
          </button>
        </div>
      </div>

      {/* Toggles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={s.manual_transfer_enabled}
            onChange={(e) => setS((p) => ({ ...p, manual_transfer_enabled: e.target.checked }))}
          />
          {lang === "es" ? "Transferencia bancaria" : "Bank transfer"}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={s.cod_enabled}
            onChange={(e) => setS((p) => ({ ...p, cod_enabled: e.target.checked }))}
          />
          {lang === "es" ? "Pago contra entrega (COD)" : "Cash on delivery (COD)"}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={s.azul_enabled}
            onChange={(e) => setS((p) => ({ ...p, azul_enabled: e.target.checked }))}
          />
          {lang === "es" ? "AZUL (tarjeta)" : "AZUL (card)"}
        </label>
      </div>

      {/* COD */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, marginBottom: 8 }}>
          <div>
            <label style={lbl}>{lang === "es" ? "Recargo COD (RD$)" : "COD surcharge (RD$)"}</label>
            <input
              type="number"
              min={0}
              style={inp}
              value={s.cod_surcharge}
              onChange={(e) => setS((p) => ({ ...p, cod_surcharge: Number(e.target.value) || 0 }))}
            />
          </div>
          <div>
            <label style={lbl}>{lang === "es" ? "Ciudades habilitadas para COD (separadas por coma)" : "COD-eligible cities (comma-separated)"}</label>
            <input
              type="text"
              style={inp}
              value={citiesText}
              onChange={(e) => setCitiesText(e.target.value)}
              placeholder="Santo Domingo, Distrito Nacional, ..."
            />
          </div>
        </div>
      </div>

      {/* Admin notification email */}
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>
          {lang === "es"
            ? "Email del admin para notificaciones de comprobantes"
            : "Admin email for receipt notifications"}
        </label>
        <input
          type="email"
          style={inp}
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          placeholder="admin@nutrimartrd.com"
        />
        <p style={{ fontSize: 10, color: "var(--tx3)", marginTop: 4 }}>
          {lang === "es"
            ? "Recibirás un email cada vez que un cliente suba un comprobante de transferencia."
            : "You'll receive an email whenever a customer uploads a transfer receipt."}
        </p>
      </div>

      {/* Express-free shipping eligibility */}
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          border: "1px dashed var(--bd2)",
          borderRadius: 8,
          background: "var(--bg)",
        }}
      >
        <label style={{ ...lbl, marginBottom: 8 }}>
          {lang === "es"
            ? "Envío Express gratis — elegibilidad"
            : "Free Express shipping — eligibility"}
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10, marginBottom: 8 }}>
          <div>
            <label style={lbl}>
              {lang === "es" ? "Subtotal mínimo (US$)" : "Minimum subtotal (US$)"}
            </label>
            <input
              type="number"
              min={0}
              step="0.01"
              style={inp}
              value={s.express_free_threshold_usd}
              onChange={(e) =>
                setS((p) => ({
                  ...p,
                  express_free_threshold_usd: Number(e.target.value) || 0,
                }))
              }
            />
            <p style={{ fontSize: 10, color: "var(--tx3)", marginTop: 4 }}>
              {lang === "es"
                ? "Si el subtotal del carrito alcanza este monto, el Express en área será gratis."
                : "If the cart subtotal reaches this amount, in-area Express ships free."}
            </p>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <div>
              <label style={lbl}>
                {lang === "es" ? "Texto explicativo (ES)" : "Explanatory text (ES)"}
              </label>
              <input
                style={inp}
                value={s.express_free_note_es ?? ""}
                onChange={(e) => setS((p) => ({ ...p, express_free_note_es: e.target.value }))}
                placeholder="El envío Express en área es gratis cuando..."
              />
            </div>
            <div>
              <label style={lbl}>
                {lang === "es" ? "Texto explicativo (EN)" : "Explanatory text (EN)"}
              </label>
              <input
                style={inp}
                value={s.express_free_note_en ?? ""}
                onChange={(e) => setS((p) => ({ ...p, express_free_note_en: e.target.value }))}
                placeholder="In-area express shipping is free when..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bank accounts */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>
            {lang === "es" ? "Cuentas bancarias" : "Bank accounts"} ({s.bank_accounts.length})
          </label>
          <button className="btn bg bs" type="button" onClick={addAcct} style={{ fontSize: 10 }}>
            + {lang === "es" ? "Agregar cuenta" : "Add account"}
          </button>
        </div>

        {s.bank_accounts.length === 0 && (
          <p style={{ fontSize: 11, color: "var(--tx3)", textAlign: "center", padding: 12 }}>
            {lang === "es"
              ? "Sin cuentas. Agrega al menos una para habilitar transferencia."
              : "No accounts. Add at least one to enable bank transfer."}
          </p>
        )}

        {s.bank_accounts.map((a, i) => (
          <div
            key={i}
            style={{
              border: "1px solid var(--bd2)",
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
              background: "var(--bg)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8 }}>
              <div>
                <label style={lbl}>{lang === "es" ? "Banco" : "Bank"}</label>
                <input
                  style={inp}
                  value={a.bank}
                  onChange={(e) => updateAcct(i, { bank: e.target.value })}
                  placeholder="Popular / BHD / Reservas"
                />
              </div>
              <div>
                <label style={lbl}>{lang === "es" ? "Tipo" : "Type"}</label>
                <input
                  style={inp}
                  value={a.account_type}
                  onChange={(e) => updateAcct(i, { account_type: e.target.value })}
                  placeholder="Corriente / Ahorros"
                />
              </div>
              <div>
                <label style={lbl}>{lang === "es" ? "Número de cuenta" : "Account number"}</label>
                <input
                  style={inp}
                  value={a.account_number}
                  onChange={(e) => updateAcct(i, { account_number: e.target.value })}
                />
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button
                  className="btn bo bs"
                  type="button"
                  onClick={() => removeAcct(i)}
                  style={{ fontSize: 10 }}
                >
                  ✗
                </button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginTop: 8 }}>
              <div>
                <label style={lbl}>{lang === "es" ? "Titular" : "Holder"}</label>
                <input
                  style={inp}
                  value={a.holder}
                  onChange={(e) => updateAcct(i, { holder: e.target.value })}
                  placeholder="NutriMarket SRL"
                />
              </div>
              <div>
                <label style={lbl}>RNC</label>
                <input
                  style={inp}
                  value={a.rnc ?? ""}
                  onChange={(e) => updateAcct(i, { rnc: e.target.value })}
                  placeholder="1-31-12345-6"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}